import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { TableReload } from '#app/Action/TableAction';
import { dispatch } from '#app/App';
import ButtonComponent from '#app/Component/ButtonComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import ApiHandler from '#app/Handler/ApiHandler';
import { Form, FormField, initFormHandler } from '#app/Handler/FormHandler';

type Props = {
  uuid: string;
  onClose: () => void;
  shop?: boolean;
};

type Product = {
  id: string;
  name: string;
  quantity: number;
};

const ReceiveModal: React.FC<Props> = ({ uuid, onClose, shop = false }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true);
  const [products, setProducts] = useState<Product[]>([]);

  const getProductsForm = (): Record<string, FormField> =>
    products.reduce(
      (fields, { id, quantity }) => {
        fields[`${id}.quantity`] = {
          name: `${id}.quantity`,
          label: 'Quantité Réceptionnée',
          textHelper: `Saisissez la quantité de produit réceptionné (maximum : ${quantity})`,
          type: 'integer',
          options: { max: quantity, validation: ['required'] },
        };

        fields[`${id}.compliant`] = {
          name: `${id}.compliant`,
          label: 'Quantité conforme',
          textHelper: `Saisissez la quantité de produit conforme (maximum : ${quantity})`,
          type: 'integer',
          options: { max: quantity, validation: ['required'] },
        };

        if (shop) {
          fields[`${id}.refund`] = {
            name: `${id}.refund`,
            label: 'Quantité Remboursé',
            textHelper: `Saisissez la quantité remboursé (maximum : ${quantity})`,
            type: 'integer',
            options: { max: quantity, validation: ['required'] },
          };
        }

        return fields;
      },
      {} as Record<string, FormField>,
    );

  const [form, setForm] = useState<Form>({
    comment: {
      name: 'comment',
      label: 'Commentaire',
      type: 'text',
    },
  });

  const handlerForm = initFormHandler(form, setForm);
  useEffect(() => {
    ApiHandler.get<Product[]>(
      { route: 'api_inventory_management_return_order_by_product_list', params: { uuid } },
      (response) => {
        setProducts(response.data ?? []);
        setLoadingProducts(false);
      },
    );
    handlerForm.start();
  }, []);

  useEffect(() => {
    if (products) {
      setForm({
        ...form,
        ...getProductsForm(),
      });
    }
  }, [products]);

  const handleSubmit = (): void => {
    if (handlerForm.checkError() > 1) {
      return;
    }
    handlerForm.setFormLoading(true);
    const data = handlerForm.getData();
    const products = Object.entries(data)
      .filter(([key]) => key.includes('.quantity'))
      .map(([key, value]) => {
        const baseKey = key.split('.')[0];
        const quantity = value ?? 0;
        const compliant = data[`${baseKey}.compliant`];
        const refund = data[`${baseKey}.refund`] ?? false;

        return {
          byProduct: parseInt(baseKey),
          quantity,
          compliant,
          refund,
        };
      });

    if (!products.some((product) => product.quantity > 0)) {
      dispatch(SnackbarOpen({ text: 'Il faut au moins un produit', variant: 'error' }));
      return;
    }

    handlerForm.setFormLoading(true);
    setLoading(true);

    ApiHandler.post(
      {
        route: 'api_inventory_management_return_order_litige_receive',
        data: {
          comment: data.comment,
          products,
        },
        params: { uuid },
      },
      (response) => {
        const success = response.status === 200;
        const errorMessage = response.error?.message || "Une erreur inattendue s'est produite.";

        dispatch(
          SnackbarOpen({
            text: success ? 'Réception enregistré avec succès.' : errorMessage,
            variant: success ? 'success' : 'error',
          }),
        );

        if (success) {
          dispatch(TableReload('api_inventory_management_return_order_list'));
          onClose();
        }
        handlerForm.setFormLoading(false);
        setLoading(false);
      },
    );
  };

  return (
    <Dialog open maxWidth="xl" onClose={onClose}>
      <DialogTitle>Réceptionner</DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2}>
          {loadingProducts && <CircularProgress size={20} />}
          {products
            .filter(({ id }) => !!form[`${id}.quantity`])
            .map(({ id, name }) => (
              <React.Fragment key={id}>
                <Grid size={12} className={classes.productLabel}>
                  {name}
                </Grid>
                <Grid size={shop ? 4 : 6}>
                  <TextFieldComponent id={`${id}.quantity`} handler={handlerForm} />
                </Grid>
                <Grid size={shop ? 4 : 6}>
                  <TextFieldComponent id={`${id}.compliant`} handler={handlerForm} />
                </Grid>
                {shop && (
                  <Grid size={4}>
                    <TextFieldComponent id={`${id}.refund`} handler={handlerForm} />
                  </Grid>
                )}
              </React.Fragment>
            ))}
          <Grid size={12}>
            <TextFieldComponent id="comment" handler={handlerForm} multiline />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonComponent color="#5E6E82" label="Annuler" onClick={onClose} disabled={loading} />
        <ButtonComponent label="Valider" loading={loading} onClick={handleSubmit} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles({
  content: {
    minWidth: 300,
    width: '90vw',
    maxWidth: 800,
  },
  productLabel: {
    paddingTop: '10px',
  },
});

export default ReceiveModal;
